import {
	AccountType,
	AddressType,
	ContractType,
	CreditBureauMaritalStatus,
	InquiryOriginIdentifier,
	PaymentFrequency,
	PaymentStatus,
	ResidenceType,
	ResponsibilityType,
	SpecialAddressIndicator,
	VerificationMethod,
	Country,
	MexState
} from '@/graphql/__generated__/onboarding';

export const getMaritalStatusText = (status: CreditBureauMaritalStatus, t: any): string => {
	const statusMap: Record<CreditBureauMaritalStatus, string> = {
		[CreditBureauMaritalStatus.DIVORCED]: t('Divorced'),
		[CreditBureauMaritalStatus.DOMESTIC_PARTNERSHIP]: t('DomesticPartnership'),
		[CreditBureauMaritalStatus.SINGLE]: t('Single'),
		[CreditBureauMaritalStatus.WIDOWED]: t('Widowed'),
		[CreditBureauMaritalStatus.MARRIED]: t('Married')
	};
	return statusMap[status] || t('Other');
};

export const getResidenceTypeText = (type: ResidenceType, t: any): string => {
	const typeMap: Record<ResidenceType, string> = {
		[ResidenceType.OWNER]: t('Owner'),
		[ResidenceType.RENTER]: t('Renter'),
		[ResidenceType.PENSION_OR_WITH_FAMILY]: t('PensionOrWithFamily')
	};
	return typeMap[type] || t('Other');
};

export const getAddressTypeText = (type: AddressType, t: any): string => {
	const typeMap: Record<AddressType, string> = {
		[AddressType.BUSINESS]: t('Business'),
		[AddressType.USER_ADDRESS]: t('UserAddress'),
		[AddressType.HOUSE]: t('House'),
		[AddressType.POSTAL_BOX]: t('PostalBox')
	};
	return typeMap[type] || t('Other');
};

export const getSpecialAddressIndicatorText = (
	indicator: SpecialAddressIndicator,
	t: any
): string => {
	const indicatorMap: Record<SpecialAddressIndicator, string> = {
		[SpecialAddressIndicator.MILITARY_ADDRESS]: t('MilitaryAddress'),
		[SpecialAddressIndicator.RURAL_ADDRESS]: t('RuralAddress'),
		[SpecialAddressIndicator.KNOWN_ADDRESS]: t('KnownAddress')
	};
	return indicatorMap[indicator] || t('Other');
};

export const getVerificationMethodText = (method: VerificationMethod, t: any): string => {
	const methodMap: Record<VerificationMethod, string> = {
		[VerificationMethod.AUTOMATIC]: t('Automatic'),
		[VerificationMethod.MANUAL]: t('Manual')
	};
	return methodMap[method] || t('Other');
};

export const getResponsibilityTypeText = (type: ResponsibilityType, t: any): string => {
	const typeMap: Record<ResponsibilityType, string> = {
		[ResponsibilityType.AUTHORIZED_USER]: t('AuthorizedUser'),
		[ResponsibilityType.INDIVIDUAL]: t('Individual'),
		[ResponsibilityType.JOINT]: t('Joint'),
		[ResponsibilityType.JOINTLY_OBLIGED]: t('JointlyObliged')
	};
	return typeMap[type] || t('Other');
};

export const getAccountTypeText = (type: AccountType, t: any): string => {
	const typeMap: Record<AccountType, string> = {
		[AccountType.FIXED_PAYMENTS]: t('FixedPayments'),
		[AccountType.WITHOUT_PRESET_LIMIT]: t('WithoutPresetLimit'),
		[AccountType.MORTGAGE]: t('Mortgage'),
		[AccountType.REVOLVING]: t('Revolving'),
		[AccountType.REPORT_ONLY]: t('ReportOnly')
	};
	return typeMap[type] || t('Other');
};

export const getPaymentFrequencyText = (frequency: PaymentFrequency, t: any): string => {
	const frequencyMap: Record<PaymentFrequency, string> = {
		[PaymentFrequency.BIMONTHLY]: t('Bimonthly'),
		[PaymentFrequency.DAILY]: t('Daily'),
		[PaymentFrequency.SEMI_ANNUALLY]: t('SemiAnnually'),
		[PaymentFrequency.BIWEEKLY]: t('Biweekly'),
		[PaymentFrequency.MONTHLY]: t('Monthly'),
		[PaymentFrequency.SALARY_DEDUCTION]: t('SalaryDeduction'),
		[PaymentFrequency.QUARTERLY]: t('Quarterly'),
		[PaymentFrequency.SEMI_MONTHLY]: t('SemiMonthly'),
		[PaymentFrequency.VARIABLE]: t('Variable'),
		[PaymentFrequency.WEEKLY]: t('Weekly'),
		[PaymentFrequency.ANNUALLY]: t('Annually'),
		[PaymentFrequency.MINIMUM_PAYMENT]: t('MinimumPayment')
	};
	return frequencyMap[frequency] || t('Other');
};

export const getInquiryOriginIdentifierText = (
	identifier: InquiryOriginIdentifier,
	t: any
): string => {
	const identifierMap: Record<InquiryOriginIdentifier, string> = {
		[InquiryOriginIdentifier.CREDIT_BUREAU]: t('CreditBureau'),
		[InquiryOriginIdentifier.CIRCLE_OF_CREDIT]: t('CircleOfCredit')
	};
	return identifierMap[identifier] || t('Other');
};

export const getContractTypeText = (type: ContractType, t: any): string => {
	const typeMap: Record<ContractType, string> = {
		[ContractType.FURNITURE_APPLIANCES]: t('FurnitureAppliances'),
		[ContractType.AGRICULTURAL]: t('Agricultural'),
		[ContractType.AUTO_LEASE]: t('AutoLease'),
		[ContractType.AVIATION]: t('Aviation'),
		[ContractType.AUTO_PURCHASE]: t('AutoPurchase'),
		[ContractType.BOND]: t('Bond'),
		[ContractType.BOAT]: t('Boat'),
		[ContractType.CREDIT_CARD]: t('CreditCard'),
		[ContractType.LETTER_OF_CREDIT]: t('LetterOfCredit'),
		[ContractType.TAX_CREDIT]: t('TaxCredit'),
		[ContractType.CREDIT_LINE]: t('CreditLine'),
		[ContractType.CONSOLIDATION]: t('Consolidation'),
		[ContractType.SIMPLE_CREDIT]: t('SimpleCredit'),
		[ContractType.COLLATERAL]: t('Collateral'),
		[ContractType.DISCOUNTS]: t('Discounts'),
		[ContractType.EQUIPMENT_TRUST]: t('EquipmentTrust'),
		[ContractType.FACTORING]: t('Factoring'),
		[ContractType.OUTFITTING_CREDIT]: t('OutfittingCredit'),
		[ContractType.HOME_EQUITY]: t('HomeEquity'),
		[ContractType.HOME_IMPROVEMENT]: t('HomeImprovement'),
		[ContractType.LEASE]: t('Lease'),
		[ContractType.REVOLVING_CREDIT_LINE]: t('RevolvingCreditLine'),
		[ContractType.OTHER]: t('Other'),
		[ContractType.OTHER_OVERDUE_DEBT]: t('OtherOverdueDebt'),
		[ContractType.BUSINESS_ACTIVITY_LOAN]: t('BusinessActivityLoan'),
		[ContractType.PUBLISHING]: t('Publishing'),
		[ContractType.INDUSTRIAL_UNITS_GUARANTEE]: t('IndustrialUnitsGuarantee'),
		[ContractType.PERSONAL_LOAN]: t('PersonalLoan'),
		[ContractType.PAYROLL_LOAN]: t('PayrollLoan'),
		[ContractType.UNSECURED_LOAN]: t('UnsecuredLoan'),
		[ContractType.PLEDGE_LOAN]: t('PledgeLoan'),
		[ContractType.SERVICES_PAYMENT]: t('ServicesPayment'),
		[ContractType.RESTRUCTURED]: t('Restructured'),
		[ContractType.REDISCOUNT]: t('Rediscount'),
		[ContractType.REAL_ESTATE]: t('RealEstate'),
		[ContractType.EQUIPMENT_LOAN]: t('EquipmentLoan'),
		[ContractType.RENEWED]: t('Renewed'),
		[ContractType.RECREATIONAL_VEHICLE]: t('RecreationalVehicle'),
		[ContractType.SECURED_CARD]: t('SecuredCard'),
		[ContractType.SECURED_LOAN]: t('SecuredLoan'),
		[ContractType.INSURANCE]: t('Insurance'),
		[ContractType.SECOND_MORTGAGE]: t('SecondMortgage'),
		[ContractType.STUDENT_LOAN]: t('StudentLoan'),
		[ContractType.BUSINESS_CREDIT_CARD]: t('BusinessCreditCard'),
		[ContractType.UNKNOWN]: t('Unknown')
	};
	return typeMap[type] || t('Other');
};

export const getMexStateText = (state: MexState, t: any): string => {
	return t(`${state}`);
};

export const getCountryText = (country: Country, t: any): string => {
	return t(`${country}`);
};

export const getPaymentStatusCode = (status: PaymentStatus | string | null | undefined): string => {
	if (!status) return '';
	const statusStr = typeof status === 'string' ? status : (status as PaymentStatus);
	if (statusStr === PaymentStatus.CODE_UR || statusStr === 'CODE_UR') return 'UR';
	if (statusStr === PaymentStatus.CODE_00 || statusStr === 'CODE_00') return '00';
	if (statusStr === PaymentStatus.CODE_01 || statusStr === 'CODE_01') return '01';
	if (statusStr === PaymentStatus.CODE_02 || statusStr === 'CODE_02') return '02';
	if (statusStr === PaymentStatus.CODE_03 || statusStr === 'CODE_03') return '03';
	if (statusStr === PaymentStatus.CODE_04 || statusStr === 'CODE_04') return '04';
	if (statusStr === PaymentStatus.CODE_05 || statusStr === 'CODE_05') return '05';
	if (statusStr === PaymentStatus.CODE_06 || statusStr === 'CODE_06') return '06';
	if (statusStr === PaymentStatus.CODE_07 || statusStr === 'CODE_07') return '07';
	if (statusStr === PaymentStatus.CODE_96 || statusStr === 'CODE_96') return '96';
	if (statusStr === PaymentStatus.CODE_97 || statusStr === 'CODE_97') return '97';
	if (statusStr === PaymentStatus.CODE_99 || statusStr === 'CODE_99') return '99';
	if (statusStr === PaymentStatus.CODE_TOTAL || statusStr === 'CODE_TOTAL') return 'Tot';
	return typeof statusStr === 'string' ? statusStr : '';
};

export const getPaymentStatusText = (
	status: PaymentStatus | string | null | undefined,
	t: any
): string => {
	if (!status) return '';
	const code = getPaymentStatusCode(status);
	const descriptionMap: Record<string, string> = {
		UR: t('PaymentStatusUR'),
		'00': t('PaymentStatus00'),
		'01': t('PaymentStatus01'),
		'02': t('PaymentStatus02'),
		'03': t('PaymentStatus03'),
		'04': t('PaymentStatus04'),
		'05': t('PaymentStatus05'),
		'06': t('PaymentStatus06'),
		'07': t('PaymentStatus07'),
		'96': t('PaymentStatus96'),
		'97': t('PaymentStatus97'),
		'99': t('PaymentStatus99'),
		Tot: t('PaymentStatusTotal')
	};
	const description = descriptionMap[code] || code;
	return `${code} - ${description}`;
};
